import { useCallback } from 'react';
import { usePartnerStore } from '../../lib/stores/partnerStore';
import { usePartnerNoteStore } from '../../lib/stores/partnerNoteStore';
import { useUserData } from '../../lib/auth';
import type { ExternalPartner } from '../../lib/types';
import { formatReferrerName } from '../../lib/utils/referrerDisplayName';

/**
 * Campi partner "extra" (non presenti sul mapping Client-like) usati dai layout
 * per la sezione dedicata (Task 3): commissioni, tariffa, specializzazione, ecc.
 */
export interface PartnerExtras {
  id?: string;
  /** undefined finche' la colonna non esiste (migration 20261016 non applicata) */
  newsletter_subscribed?: boolean;
  commission_rate?: number;
  hourly_rate?: number;
  specialization?: string;
  business_sector?: string;
  performance_rating?: number;
  collaboration_start_date?: string;
  partner_type?: ExternalPartner['partner_type'];
  status?: ExternalPartner['status'];
}

/**
 * Mappa un `ExternalPartner` in un oggetto "Client-like" per riutilizzare, senza
 * modificarle, le property rows agnostiche di LeadDesktopLayout/LeadMobileLayout
 * (che leggono `selectedClient` tipizzato `any`).
 *
 * Mapping campi per campo, come da task-2-brief.md:
 * business_name←name, trade_name←company, email, phone, website, street_address,
 * city, postal_code, country, vat_number, tax_code, general_comments←notes(legacy),
 * created_at, logo_url←null.
 */
export function mapPartnerToClientView(partner: ExternalPartner): Record<string, unknown> {
  return {
    id: partner.id,
    organization_id: partner.organization_id,
    entity_type: 'company',
    // Solo a video, come i segnalatori: il nome si modifica dal form partner sul valore salvato
    business_name: formatReferrerName(partner.name),
    trade_name: partner.company,
    email: partner.email,
    phone: partner.phone,
    website: partner.website,
    street_address: partner.street_address,
    city: partner.city,
    postal_code: partner.postal_code,
    country: partner.country,
    vat_number: partner.vat_number,
    tax_code: partner.tax_code,
    general_comments: partner.notes,
    created_at: partner.created_at,
    logo_url: null,
  };
}

function mapPartnerExtras(partner: ExternalPartner): PartnerExtras {
  return {
    id: partner.id,
    newsletter_subscribed: partner.newsletter_subscribed,
    commission_rate: partner.commission_rate,
    hourly_rate: partner.hourly_rate,
    specialization: partner.specialization,
    business_sector: partner.business_sector,
    performance_rating: partner.performance_rating,
    collaboration_start_date: partner.collaboration_start_date,
    partner_type: partner.partner_type,
    status: partner.status,
  };
}

/**
 * Helper per la modalità partner di UnifiedDetailsPage (Task 2, piano "Partner
 * drawer unificato"). Incapsula fetch partner + note partner + mapping
 * view-model, cosi' da non gonfiare LeadDetailsPage.tsx.
 *
 * Non fa nulla se `partnerId` è undefined (nessun fetch, nessuna sottoscrizione):
 * questo mantiene il comportamento invariato quando la pagina è usata in
 * modalità lead/client (partnerId assente).
 */
export function usePartnerEntity(partnerId: string | undefined) {
  const { organizationId, userId } = useUserData();

  const { selectedPartner: partner, fetchPartnerById, loading: partnerLoading, error: partnerError } = usePartnerStore();

  const {
    notes,
    loading: notesLoading,
    fetchNotesByPartnerId,
    createNote: createPartnerNote,
    updateNote,
    deleteNote,
  } = usePartnerNoteStore();

  const fetchPartner = useCallback(async () => {
    if (!partnerId) return;
    await fetchPartnerById(partnerId);
  }, [partnerId, fetchPartnerById]);

  const fetchNotes = useCallback(async () => {
    if (!partnerId) return;
    await fetchNotesByPartnerId(partnerId);
  }, [partnerId, fetchNotesByPartnerId]);

  const createNote = useCallback(async (content: string) => {
    if (!partnerId || !organizationId || !userId) return null;
    return createPartnerNote(partnerId, content, organizationId, userId);
  }, [partnerId, organizationId, userId, createPartnerNote]);

  const partnerView = partner && partner.id === partnerId ? mapPartnerToClientView(partner) : null;
  const partnerExtras = partner && partner.id === partnerId ? mapPartnerExtras(partner) : null;

  return {
    partner: partner && partner.id === partnerId ? partner : null,
    partnerView,
    partnerExtras,
    partnerLoading,
    partnerError,
    fetchPartner,
    notes,
    notesLoading,
    fetchNotes,
    createNote,
    updateNote,
    deleteNote,
    organizationId,
    userId,
  };
}
